/** The tour record's own widgets (plan §2): the cover / meeting-point image and the
 *  meeting point's coordinates on the records widgets (a staged file; two paths, one undo
 *  step, the map in a popover), the length, and the remote pickers bound to the
 *  operator's tour pickers. */
import { Box, Checkbox, TextField, Typography } from '@mui/material';
import { useRef, useState } from 'react';

import * as toursApi from '@/api/tours';
import type { PickerKind, TourDetail } from '@/api/tours';
import { useDraft, useDraftField } from '@/features/records/draft/DraftProvider';
import type { RecordOptionRef, RecordWidget, RecordWidgetProps } from '@/features/records/editor/types';
import { createCoordinatesWidget } from '@/features/records/fields/widgets/CoordinatesWidget';
import { ImageThumb, ImageWidget, type StoredImage } from '@/features/records/fields/widgets/ImageWidget';
import { createRecordPicker, createTagsWidget } from '@/features/records/fields/widgets/pickers';
import { TimezoneWidget } from '@/features/records/fields/widgets/TimezoneWidget';

import { parseLength, splitLength } from './length';
import { useTourRecord } from './TourRecordContext';

// ---------------------------------------------------------------------------
// Images (cover, meeting point photo)
// ---------------------------------------------------------------------------

export function storedImageOf(detail: TourDetail, path: string): StoredImage | null {
  if (path === 'cover_image') {
    const c = detail.cover_image;
    return c ? { id: c.id, url: c.url, p: c.p, s: c.s, name: c.image_name } : null;
  }
  if (path === 'meeting_point.photo') return detail.meeting_point?.photo ?? null;
  return null;
}

/** The view-mode value of an image path: the stored thumbnail and its name. */
export function ImageDisplay({ path, label }: { path: string; label: string }) {
  const { detail } = useTourRecord();
  const image = storedImageOf(detail, path);
  if (!image) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 48, minWidth: 0 }}>
      <ImageThumb image={image} label={label} />
      {image.name && (
        <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
          {image.name}
        </Typography>
      )}
    </Box>
  );
}

/** An image path on the records ImageWidget: the stored image from the detail, a picked
 *  file staged on the page (sent with the save's multipart PATCH). */
function TourImageWidget(p: RecordWidgetProps) {
  const { detail, files } = useTourRecord();
  return <ImageWidget {...p} stored={storedImageOf(detail, p.field.key)} staging={{ kind: 'local', files }} />;
}

// ---------------------------------------------------------------------------
// Coordinates (meeting_point.latitude + meeting_point.longitude)
// ---------------------------------------------------------------------------

export const LAT_PATH = 'meeting_point.latitude';
export const LNG_PATH = 'meeting_point.longitude';

const CoordinatesWidget = createCoordinatesWidget({ lat: LAT_PATH, lng: LNG_PATH, place: 'meeting point' });

// ---------------------------------------------------------------------------
// Remote pickers (the operator's tour pickers)
// ---------------------------------------------------------------------------

async function pick(
  tenantId: number,
  kind: PickerKind,
  q: string,
  group?: (o: toursApi.PickerOption) => string | null,
  tourId?: number,
) {
  const r = await toursApi.picker(tenantId, kind, q.trim() || undefined, undefined, kind === 'tour_users' ? tourId : undefined);
  return r.results.map<RecordOptionRef>((o) => ({
    id: o.id,
    label: o.name ?? `#${o.id}`,
    ...(group ? { group: group(o) } : {}),
    // A policy names its tenant (its own, its franchise parent's, or none: global).
    ...(o.tenant !== undefined ? { tenant: o.tenant?.code ?? null } : {}),
  }));
}

/** Tours of the operator (an alias's "Also books"), without the tour itself. */
async function pickTours(tenantId: number, tourId: number, q: string): Promise<RecordOptionRef[]> {
  const r = await toursApi.list({ tour_operator_id: tenantId, pageSize: 25, ...(q.trim() ? { search: q.trim() } : {}) });
  return r.results
    .filter((t) => t.id !== tourId)
    .map((t) => ({ id: t.id, label: t.name, group: 'Tours', tenant: t.tenant?.code ?? null, extra: [t.custom_id] }));
}

/** The picker behind each id-set field, and how its options group. */
export const ID_SET_PICKERS: Record<
  string,
  { kind: PickerKind; group: (o: toursApi.PickerOption) => string | null; placeholder: string }
> = {
  atag: { kind: 'atags', group: () => 'Access tags', placeholder: 'Add an access tag' },
  tags: { kind: 'tags', group: (o) => o.code ?? 'Other', placeholder: 'Add a tag' },
  crosssells: { kind: 'crosssells', group: () => 'Packages', placeholder: 'Add a package' },
  'alias.tour_user_tags': { kind: 'tags', group: (o) => o.code ?? 'Other', placeholder: 'Add a tag' },
  departures: { kind: 'departure_tags', group: () => 'Departures', placeholder: 'Add a departure time' },
  'pretrip.roles': { kind: 'roles', group: () => 'Roles', placeholder: 'Add a role' },
  'pretrip.agroups': { kind: 'access_groups', group: () => 'Access groups', placeholder: 'Add an access group' },
  // `code`: the booking's role name; none = a traveler.
  'pretrip.users': { kind: 'tour_users', group: (o) => o.code ?? 'Travelers', placeholder: 'Add a user on this tour' },
};

/** The tour's picker widgets, bound to one operator — create once per tenant. */
export function tourPickerWidgets(tenantId: number, tourId = 0): Record<string, RecordWidget> {
  return {
    launch_policy: createRecordPicker({
      load: (_f, _r, q) => pick(tenantId, 'launch_policies', q),
      fetchOnEmpty: true,
      placeholder: 'Operator default',
    }),
    route_set: createRecordPicker({
      load: (_f, _r, q) => pick(tenantId, 'route_sets', q),
      fetchOnEmpty: true,
      placeholder: 'No line set',
    }),
    // One `tags` widget for the tour's id sets — the ledger diffs any `tags` field as
    // added / removed — searching the picker of the field it edits.
    tags: createTagsWidget({
      load: (field, _r, q) => {
        if (field.key === 'alias.targets') return pickTours(tenantId, tourId, q);
        const set = ID_SET_PICKERS[field.key];
        return set ? pick(tenantId, set.kind, q, set.group, tourId) : Promise.resolve([]);
      },
      placeholder: (field) =>
        field.key === 'alias.targets' ? 'Add a tour' : (ID_SET_PICKERS[field.key]?.placeholder ?? 'Add a tag'),
    }),
  };
}

// ---------------------------------------------------------------------------
// Length (length + length_is_manual, one choice)
// ---------------------------------------------------------------------------

let lengthSession = 0;

/** "Set by hand" and the hours and minutes, in one row the height of an input. Off: the
 *  derived length shows, disabled (the stored value is kept). One undo step per toggle;
 *  typing coalesces per focus session. */
export function LengthWidget(p: RecordWidgetProps) {
  const { dispatch, baselineOf } = useDraft();
  const id = Number(p.rec.slice(p.rec.lastIndexOf(':') + 1));
  const manual = useDraftField('tour', id, 'length_is_manual');
  const isManual = manual.value === true;
  const [typed, setTyped] = useState<{ hours: string; minutes: string } | null>(null);
  const shown = typed ?? splitLength(p.value);
  const session = useRef<string | null>(null);

  const set = (next: { length: unknown; manual: boolean }, label?: string) =>
    dispatch({
      type: 'SET',
      changes: [
        { rec: p.rec, path: 'length', from: baselineOf(p.rec, 'length'), to: next.length },
        { rec: p.rec, path: 'length_is_manual', from: baselineOf(p.rec, 'length_is_manual'), to: next.manual },
      ],
      ...(label ? { label } : {}),
      ...(session.current ? { coalesce: session.current } : {}),
    });
  const onType = (part: 'hours' | 'minutes', v: string) => {
    const next = { ...shown, [part]: v };
    setTyped(next);
    const parsed = parseLength(next.hours, next.minutes);
    if (parsed === 'invalid' || parsed === null) p.onInvalid('Enter whole hours and 0–59 minutes, at least a minute');
    else {
      p.onInvalid(null);
      set({ length: parsed, manual: true });
    }
  };
  const input = (part: 'hours' | 'minutes', label: string, unit: string) => (
    <TextField
      size="small"
      value={shown[part]}
      disabled={p.disabled || !isManual}
      error={p.error}
      onChange={(e) => onType(part, e.target.value)}
      onFocus={() => {
        lengthSession += 1;
        session.current = `${p.rec}|length#${lengthSession}`;
      }}
      onBlur={() => {
        session.current = null;
        setTyped(null);
      }}
      inputProps={{ 'aria-label': label, inputMode: 'numeric', 'aria-describedby': p.describedBy, autoComplete: 'off' }}
      InputProps={{ endAdornment: <Typography variant="caption" color="text.secondary">{unit}</Typography> }}
      sx={{ flex: '1 1 0', minWidth: 0 }}
    />
  );
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
      <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', flex: 'none', cursor: p.disabled ? 'default' : 'pointer' }}>
        <Checkbox
          size="small"
          checked={isManual}
          disabled={p.disabled}
          onChange={(e) =>
            set(
              e.target.checked
                ? { length: p.value, manual: true }
                : { length: baselineOf(p.rec, 'length'), manual: false },
              e.target.checked ? 'Set the length by hand' : 'Derive the length again',
            )
          }
          inputProps={{ id: p.id, 'aria-describedby': p.describedBy }}
          sx={{ p: 0.5 }}
        />
        <Typography variant="body2">By hand</Typography>
      </Box>
      {input('hours', 'Hours', 'h')}
      {input('minutes', 'Minutes', 'min')}
    </Box>
  );
}

export const TOUR_STATIC_WIDGETS: Record<string, RecordWidget> = {
  timezone: TimezoneWidget,
  image: TourImageWidget,
  coordinates: CoordinatesWidget,
  length: LengthWidget,
};
